'use client';

import React, {
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { faUserDoctor } from '@fortawesome/pro-solid-svg-icons';
import { useGetAllStrategiesFilterOptionsQuery } from 'src/api/redux/strategy/allStrategiesApi';
import { useDebounce } from 'use-debounce';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';
import { useLogging } from 'src/context/logging';
import {
  useClickOutside,
  useDropdownPosition,
  useResizeClose,
} from 'src/hooks/common';
import {
  FilterDropdownButton,
  FilterDropdownPortal,
  CheckboxListItem,
  DropdownEmptyState,
} from '../shared';

interface Specialty {
  id: string;
  name: string;
}

const StrategySpecialtyDropdown: React.FC = (): React.ReactElement => {
  const log = useLogging();
  const {
    boundedTerritory,
    selectedTerritory,
    selectedChainIds,
    selectedSpecialties,
    setSelectedSpecialties,
  } = useStrategies(LOCATION.STRATEGY_CENTER);
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [debouncedSearch] = useDebounce(search, 500);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [allSpecialties, setAllSpecialties] = useState<Specialty[]>([]);
  const isLoadingNextPage = useRef<boolean>(false);

  const { data: specialties, isFetching } =
    useGetAllStrategiesFilterOptionsQuery(
      {
        type: 'specialty',
        chain_id: selectedChainIds,
        territory_id: selectedTerritory?.id,
        bounding_territory: boundedTerritory?.id,
        q: debouncedSearch,
        page,
        pageSize,
      },
      { skip: selectedChainIds.length === 0 }
    );

  const hasMore = specialties?.results?.length === pageSize;

  const { dropdownStyle, calculatePosition } = useDropdownPosition(
    buttonRef,
    300
  );

  useClickOutside([buttonRef, dropdownRef], () => setIsOpen(false), isOpen);
  useResizeClose(() => setIsOpen(false), isOpen);

  useEffect(() => {
    setPage(1);
    setAllSpecialties([]);
    isLoadingNextPage.current = false;
  }, [debouncedSearch, selectedChainIds, boundedTerritory?.id, selectedTerritory?.id]);

  useEffect(() => {
    if (specialties?.results) {
      setAllSpecialties((prev) => {
        const validSpecialties = specialties.results.filter((s: Specialty) => s.name);
        if (page === 1) {
          isLoadingNextPage.current = false;
          return validSpecialties;
        }
        const existingIds = new Set(prev.map((s: Specialty) => s.id));
        const newSpecialties = validSpecialties.filter(
          (s: Specialty) => !existingIds.has(s.id)
        );
        isLoadingNextPage.current = false;
        return [...prev, ...newSpecialties];
      });
    }
  }, [specialties, page]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || isFetching || !hasMore || isLoadingNextPage.current)
      return;

    const { scrollTop, scrollHeight, clientHeight } = container;

    if (scrollTop + clientHeight >= scrollHeight - 100) {
      isLoadingNextPage.current = true;
      setPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !isOpen) return;

    container.addEventListener('scroll', handleScroll);
    return () => container.removeEventListener('scroll', handleScroll);
  }, [handleScroll, isOpen]);

  const isDisabled = selectedChainIds.length === 0;

  const handleClick = () => {
    if (isDisabled) return;

    if (!isOpen) {
      calculatePosition();
    }

    setIsOpen(!isOpen);
  };

  const handleSpecialtyToggle = (specialtyId: string) => {
    const updatedSpecialties = selectedSpecialties.includes(specialtyId)
      ? selectedSpecialties.filter((s) => s !== specialtyId)
      : [...selectedSpecialties, specialtyId];
    setSelectedSpecialties(updatedSpecialties);

    const specialty = allSpecialties.find((s) => s.id === specialtyId);
    log.event('Strategy filter specialty toggled', {
      source: 'StrategySpecialtyDropdown',
      specialtyId,
      specialtyName: specialty?.name,
      action: selectedSpecialties.includes(specialtyId) ? 'removed' : 'added',
      totalSpecialties: updatedSpecialties.length,
      selectedChainIds,
    });
  };

  const handleClearSpecialties = (e: React.MouseEvent) => {
    e.stopPropagation();
    const previousCount = selectedSpecialties.length;
    setSelectedSpecialties([]);

    log.event('Strategy filter specialties cleared', {
      source: 'StrategySpecialtyDropdown',
      clearedCount: previousCount,
      selectedChainIds,
    });
  };

  const getDisplayText = () => {
    if (selectedSpecialties.length === 0) return 'Specialties';
    if (selectedSpecialties.length === 1) {
      return (
        allSpecialties.find((s) => s.id === selectedSpecialties[0])?.name ||
        selectedSpecialties[0]
      );
    }
    return `${selectedSpecialties.length} Specialties`;
  };

  return (
    <>
      <FilterDropdownButton
        icon={faUserDoctor}
        label="Specialty"
        isOpen={isOpen}
        isDisabled={isDisabled}
        displayText={getDisplayText()}
        hasSelection={selectedSpecialties.length > 0}
        onClear={handleClearSpecialties}
        onClick={handleClick}
        buttonRef={buttonRef}
        testId="strategy-specialty-dropdown"
      />

      <FilterDropdownPortal
        isOpen={isOpen}
        dropdownRef={dropdownRef}
        scrollContainerRef={scrollContainerRef}
        dropdownStyle={dropdownStyle}
        searchPlaceholder="Search specialties..."
        searchValue={search}
        onSearchChange={setSearch}
        selectedCount={selectedSpecialties.length}
        onClearAll={() => setSelectedSpecialties([])}
      >
        {allSpecialties.map((specialty) => (
          <CheckboxListItem
            key={specialty.id}
            id={specialty.id}
            label={specialty.name}
            isSelected={selectedSpecialties.includes(specialty.id)}
            onToggle={handleSpecialtyToggle}
          />
        ))}

        <DropdownEmptyState
          hasResults={allSpecialties.length > 0}
          isLoading={isFetching}
          searchTerm={search}
          emptyMessage="specialties"
        />
      </FilterDropdownPortal>
    </>
  );
};

export default StrategySpecialtyDropdown;
